import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { fetchCallHistory } from '@/api/callHistoryApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import { formatPhoneNumber, displayOrBlank, EMPTY_DISPLAY } from '@/utils/format'

const SEARCH_FIELDS = [
  'from_number',
  'caller_name',
  'start_time',
  'duration_seconds',
  'call_status',
  'call_direction',
]

function resolveCallerName(raw) {
  const candidates = [
    raw?.caller_name,
    raw?.callerName,
    raw?.user_name,
    raw?.userName,
    raw?.contact_name,
    raw?.contactName,
    raw?.name,
  ]
  for (const value of candidates) {
    const name = displayOrBlank(value, '')
    if (name) return name
  }
  return EMPTY_DISPLAY
}

function normalizeCallDirection(value) {
  const key = String(value ?? '').trim().toLowerCase()
  if (key === 'inbound' || key === 'incoming') return 'inbound'
  if (key === 'outbound' || key === 'outgoing') return 'outbound'
  return null
}

function normalizeCallStatus(value) {
  const status = String(value ?? '').trim()
  return status || null
}

function normalizeCallRow(raw, idx) {
  return {
    id: raw.uuid ?? raw.caller_id ?? raw.recording_id ?? `call-${idx}`,
    from_number: raw.from_number
      ? formatPhoneNumber(raw.from_number)
      : '—',
    did: raw.did ? formatPhoneNumber(raw.did) : '—',
    caller_name: resolveCallerName(raw),
    start_time: raw.start_time ?? null,
    end_time: raw.end_time ?? null,
    duration_seconds: raw.duration_seconds ?? null,
    call_status: normalizeCallStatus(raw.call_status ?? raw.callStatus),
    call_direction: normalizeCallDirection(
      raw.call_direction ?? raw.callDirection,
    ),
    recording_s3_key: raw.recording_s3_key ?? null,
    recording_s3_url: raw.recording_s3_url ?? null,
    recording_status: raw.recording_status ?? null,
    credits_balance_before: raw.credits_balance_before ?? null,
    credits_balance_after: raw.credits_balance_after ?? null,
    call_cost: raw.call_cost ?? null,
    transcript: raw.transcript ?? null,
    ai_engine: raw.ai_engine ?? null,
    org_name: raw.org_name ?? null,
  }
}

export { hasCallRecording } from '@/utils/callRecording'

// KAN-31: taskId (optional, a Ref) scopes every fetch to one campaign --
// used both by CampaignCallHistoryPanel.vue (a fixed ref, never changes
// after mount) and CallHistoryPage.vue's own new campaign filter (a
// reactive ref the admin can change via a dropdown, refetching on each
// change). Passing nothing keeps this exactly the org-wide behavior it
// always had.
export function useCallHistoryData(options = {}) {
  const { taskId } = options
  const store = useStore()
  const route = useRoute()

  const calls = ref([])
  const loading = ref(false)
  const page = ref(1)
  const itemsPerPage = ref(25)

  const isSessionReady = computed(() => store.getters.isSessionReady)
  const sessionContext = computed(() => store.getters.sessionContext)
  const organization = computed(() => store.state.organization)
  const tableSearch = computed(() => store.state.tableSearch)

  const callRows = computed(() => {
    const rows = Array.isArray(calls.value) ? calls.value : []
    return rows.map(normalizeCallRow)
  })

  const filteredCalls = computed(() => {
    const q = (tableSearch.value || '').trim().toLowerCase()
    if (!q) return callRows.value
    return callRows.value.filter((item) =>
      SEARCH_FIELDS.some((field) =>
        String(item[field] ?? '')
          .toLowerCase()
          .includes(q),
      ),
    )
  })

  const totalCalls = computed(() => filteredCalls.value.length)

  const toNumberDisplay = computed(() => {
    const orgNumber = String(
      organization.value?.sip_number ||
        organization.value?.assigned_sip_number ||
        '',
    ).trim()
    if (orgNumber) return formatPhoneNumber(orgNumber)

    for (const row of callRows.value) {
      const did = String(row.did ?? '').trim()
      if (did && did !== '—') return did
    }
    return '—'
  })

  const pageCount = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return 1
    return Math.max(1, Math.ceil(filteredCalls.value.length / perPage))
  })

  const paginatedCalls = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return filteredCalls.value
    const start = (page.value - 1) * perPage
    return filteredCalls.value.slice(start, start + perPage)
  })

  const rangeLabel = computed(() => {
    const total = filteredCalls.value.length
    if (!total) return '0 of 0'
    const perPage = itemsPerPage.value
    if (perPage <= 0) return `1-${total} of ${total}`
    const start = (page.value - 1) * perPage + 1
    const end = Math.min(page.value * perPage, total)
    return `${start}-${end} of ${total}`
  })

  const isSearchActive = computed(() => Boolean((tableSearch.value || '').trim()))

  function buildCallHistoryInput() {
    const { org_id } = sessionContext.value ?? {}
    const input = org_id ? { org_id } : {}
    const tid = taskId?.value
    if (tid) input.task_id = tid
    return input
  }

  async function loadCallHistory() {
    if (!isSessionReady.value) return { ok: false }

    loading.value = true
    try {
      const response = await fetchCallHistory(buildCallHistoryInput())
      const rows = unwrapApiList(response)

      if (rows.length) {
        calls.value = rows
        page.value = 1
        return { ok: true }
      }

      if (!isApiSuccess(response)) {
        calls.value = []
        return response
      }

      calls.value = rows
      page.value = 1
      return { ok: true }
    } catch (err) {
      // Return an error-shaped result instead of throwing -- this function
      // is also called fire-and-forget from watchers below (immediate
      // watchers, a taskId change) that never catch a rejection. Re-
      // throwing here meant "no call history yet" (a normal, expected
      // empty state, not a real failure) surfaced as an unhandled promise
      // rejection on every such campaign, which is exactly the "Uncaught
      // runtime errors" dev overlay Ramu reported (2026-09-04). The two
      // real callers that explicitly await this (CampaignCallHistoryPanel.
      // vue, CallHistoryPage.vue) already check `result.status === 'ERROR'`
      // as their primary path -- this satisfies that directly, no behavior
      // change for them.
      calls.value = []
      return { status: 'ERROR', message: err?.message || 'Failed to load call history' }
    } finally {
      loading.value = false
    }
  }

  function setPage(next) {
    page.value = Math.min(Math.max(1, next), pageCount.value)
  }

  function setItemsPerPage(value) {
    itemsPerPage.value = value
    page.value = 1
  }

  watch(filteredCalls, () => {
    if (page.value > pageCount.value) {
      page.value = pageCount.value
    }
  })

  watch(
    isSessionReady,
    (ready) => {
      if (ready) loadCallHistory()
    },
    { immediate: true },
  )

  watch(
    () => route.name,
    (name) => {
      if (name === 'CallHistory' && isSessionReady.value) {
        loadCallHistory()
      }
    },
  )

  watch(tableSearch, () => {
    page.value = 1
  })

  // Only relevant when the caller passed a reactive taskId (the global
  // page's own campaign filter) -- CampaignCallHistoryPanel.vue's ref never
  // changes after mount, so this is a no-op there.
  if (taskId) {
    watch(taskId, () => {
      if (isSessionReady.value) loadCallHistory()
    })
  }

  return {
    calls,
    loading,
    page,
    itemsPerPage,
    pageCount,
    isSessionReady,
    filteredCalls,
    paginatedCalls,
    totalCalls,
    toNumberDisplay,
    rangeLabel,
    isSearchActive,
    loadCallHistory,
    setPage,
    setItemsPerPage,
  }
}

export function getCallHistoryErrorMessage(payload) {
  return getApiMessage(payload, 'Failed to load call history')
}
